import { JSX, PropsWithChildren } from 'react';

import { cn, Text } from '@videoblocks/design-system';

/**
 * Rendered width of a full-bleed image in this panel, derived from the widths subtracted below it:
 * the side nav (96px), the side panel (272px -> 484px at xl), and this panel's own p-5, which 2xl
 * drops in favour of max-w-[80%]. Keep in sync with the class names in this file, AiToolkitSideNavBar,
 * and AiToolkitSidePanel.
 */
export const AI_TOOLKIT_MAIN_PANEL_IMAGE_SIZES =
    '(min-width: 1600px) calc(80vw - 464px), (min-width: 1200px) calc(100vw - 620px), (min-width: 672px) calc(100vw - 408px), calc(100vw - 40px)';

export interface AiToolkitMainPanelProps extends PropsWithChildren {
    heading: string;
    icon?: React.ReactNode;
    className?: string;
    innerContainerClassName?: string;
}

/**
 * Last of the 3 column layout used primarily in page.tsx
 */
export const AiToolkitMainPanel = ({
    children,
    heading,
    icon,
    className,
    innerContainerClassName = '',
}: AiToolkitMainPanelProps): JSX.Element => {
    return (
        <div
            id="ai-toolkit-main-panel"
            className={cn(
                'scrollbar-thin-dark min-w-0 w-full flex flex-col max-h-[calc(100vh-132px)] topnav-md:max-h-[calc(100vh-67px)]',
                className,
            )}
        >
            <div className="w-full flex p-5 border-b border-r dark:border-neutral-dark-200">
                <div className="flex items-center gap-3">
                    {icon}
                    <Text asChild fontSize="sm">
                        <h1>{heading}</h1>
                    </Text>
                </div>
            </div>
            <div className={'w-full h-full flex justify-center overflow-y-auto mb-4'}>
                <div className={cn('w-full 2xl:max-w-[80%] p-5 2xl:py-5 2xl:px-0', innerContainerClassName)}>
                    {children}
                </div>
            </div>
        </div>
    );
};
